import React, { FC, useEffect, useState, useRef, useMemo } from 'react'
import { toast } from 'react-toastify'

type Props = {
  visible: boolean
  records: any[]
  period: string
  onClose: () => void
  onReviewAll: (masterRemarks: string, period: string, file?: File | null) => void
  isLoading?: boolean
}

const WRAP_STYLE: React.CSSProperties = {
  background: 'rgba(0,0,0,0.5)',
  position: 'fixed',
  inset: 0,
  zIndex: 1050,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const LABEL = 'form-label fw-semibold fs-6 mb-2 text-dark'

const AllotmentMasterBulkReviewModal: FC<Props> = ({
  visible,
  records,
  period,
  onClose,
  onReviewAll,
  isLoading = false,
}) => {
  const [selectedMonth, setSelectedMonth] = useState('')
  const [masterRemarks, setMasterRemarks] = useState('')
  const [remarksError, setRemarksError] = useState('')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!visible) return
    const initialMonth =
      period && period !== 'Selected Month'
        ? period.slice(0, 7)
        : records[0]?.statementMonth
        ? records[0].statementMonth.slice(0, 7)
        : new Date().toISOString().slice(0, 7)
    setSelectedMonth(initialMonth)
    setMasterRemarks('')
    setRemarksError('')
    setSelectedFile(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }, [visible, period, records])

  const filteredRecords = useMemo(() => {
    if (!selectedMonth) return records
    return records.filter((r) => r.statementMonth && r.statementMonth.slice(0, 7) === selectedMonth)
  }, [records, selectedMonth])

  const validateRemarks = () => {
    if (!masterRemarks || !masterRemarks.trim()) {
      setRemarksError('Master remarks are required.')
      return false
    }
    setRemarksError('')
    return true
  }

  if (!visible) return null

  return (
    <div style={WRAP_STYLE} tabIndex={-1}>
      <div
        className='bg-white text-dark rounded shadow-lg d-flex flex-column'
        style={{
          width: '70vw',
          maxWidth: '900px',
          maxHeight: '85vh',
        }}
      >
        <div className='d-flex align-items-center justify-content-between border-bottom px-5 py-4 flex-shrink-0'>
          <div>
            <h5 className='modal-title text-dark m-0'>Bulk Review - Master</h5>
            <div className='text-muted small mt-1'>
              Review crew allotments by month
            </div>
          </div>
          <div className='d-flex align-items-center gap-3'>
            <span className='badge badge-light-warning'>{filteredRecords.length} Pending</span>
            <button type='button' className='btn-close m-0' onClick={onClose} disabled={isLoading} />
          </div>
        </div>

        <div className='flex-grow-1 overflow-auto px-5 py-4'>
          {/* Month / Period Selector */}
          <div className='mb-4'>
            <label className={LABEL}>Month / Year Filter</label>
            <input
              type='month'
              className='form-control'
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              disabled={isLoading}
            />
          </div>

          {/* Crew List */}
          <div className='mb-4'>
            <label className='form-label fw-semibold fs-6 mb-2 text-dark'>
              Crew Members to Review ({filteredRecords.length}):
            </label>
            <div className='border rounded p-3' style={{ maxHeight: '200px', overflowY: 'auto' }}>
              {filteredRecords.length === 0 ? (
                <div className='text-muted text-center py-3'>
                  No pending allotments to review for {selectedMonth || 'selected filters'}
                </div>
              ) : (
                <table className='table table-sm table-row-bordered'>
                  <thead>
                    <tr className='fw-bold text-muted'>
                      <th>#</th>
                      <th>Crew Name</th>
                      <th>Rank</th>
                      <th>Vessel</th>
                      <th>Month</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRecords.map((record, index) => (
                      <tr key={record.allotmentId || record.id || index}>
                        <td>{index + 1}</td>
                        <td>{record.crewName || record.beneficiaryName || record.crewId?.toString() || '-'}</td>
                        <td>{record.rankName || '-'}</td>
                        <td>{record.vesselName || '-'}</td>
                        <td>{record.statementMonth ? record.statementMonth.slice(0, 7) : '-'}</td>
                        <td>{record.workflowStatus || 'PENDING_MASTER_REVIEW'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Master Remarks */}
          <div className='mb-4'>
            <label className={LABEL}>
              Master Remarks (applies to all) <span style={{ color: '#dc3545' }}>*</span>
            </label>
            <textarea
              className='form-control'
              rows={3}
              value={masterRemarks}
              onChange={(event) => {
                setMasterRemarks(event.target.value)
                if (remarksError) setRemarksError('')
              }}
              placeholder='Add master review remarks for all crew members'
              disabled={isLoading}
            />
            {remarksError && <div className='text-danger small mt-2'>{remarksError}</div>}
          </div>

          {/* Attachment */}
          <div className='mb-4'>
            <label className={LABEL}>Attachment (optional — applies to all)</label>
            <input
              ref={fileInputRef}
              type='file'
              className='form-control'
              onChange={(e) => {
                const file = e.target.files?.[0] || null
                if (file && file.size > 10 * 1024 * 1024) {
                  toast.error('File size exceeds 10MB limit')
                  if (fileInputRef.current) fileInputRef.current.value = ''
                  setSelectedFile(null)
                  return
                }
                setSelectedFile(file)
              }}
              disabled={isLoading}
            />
            {selectedFile && (
              <div className='small text-muted mt-1'>
                Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
              </div>
            )}
          </div>

          {/* Warning */}
          <div className='alert alert-danger d-flex align-items-start m-0'>
            <i className='bi bi-exclamation-triangle-fill me-2 mt-1 fs-5'></i>
            <div>
              <strong>Important Note:</strong> Once the bulk master review is submitted, these allotments will be forwarded to the office for review. 
              You cannot undo this action after submission. Please verify all entries before proceeding.
            </div>
          </div>
        </div>

        <div className='border-top d-flex justify-content-end gap-2 px-5 py-4 flex-shrink-0'>
          <button type='button' className='btn btn-light btn-sm' onClick={onClose} disabled={isLoading}>
            Cancel
          </button>
          <button
            type='button'
            className='btn btn-success btn-sm'
            disabled={isLoading || filteredRecords.length === 0}
            onClick={() => {
              if (!validateRemarks()) return
              onReviewAll(masterRemarks, selectedMonth, selectedFile)
            }}
          >
            {isLoading ? 'Reviewing...' : `Approve All (${filteredRecords.length})`}
          </button>
        </div>
      </div>
    </div>
  )
}

export default AllotmentMasterBulkReviewModal
